import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import LoadingButton from "@mui/lab/LoadingButton";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { SelectChangeEvent } from '@mui/material/Select';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { isEmpty } from 'lodash';

import { IUserDetails, IUserFormInputs } from '../../utils/user.types';
import CustomTextField from '../../components/FormFields/CustomTextField';
import CustomSelect, { IOptions } from '../../components/FormFields/CustomSelect';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ApiStatus } from '../../utils/api.types';
import { addUser, updateUser } from '../../store/userThunk';
import RoleService from '../../services/RoleService';
import CustomCheckbox from '../../components/FormFields/CustomCheckbox';
import BreadCrum from "../../components/Breadcrumbs/Breadcrumbs";

// Schema to validate user form.
const schema = yup.object().shape({
    name: yup.string().required('Name is required.'),
    email: yup.string().required('Owner email is required.'),
    isActive: yup.boolean(),
    roles: yup.array().of(yup.string()).nullable(),
});

interface ILocationState {
    accountId: string;
}

/**
 * Component to create/edit user.
 */
const UserForm = () => {
    const { userId = '' } = useParams();
    const { state } = useLocation();

    // use form hook to mange user form state.
    const methods = useForm<IUserFormInputs>({
        resolver: yupResolver(schema),
    });

    // States
    const [roleOptions, setRoleOptions] = useState<IOptions[]>([]);
    const [userRoles, setUserRoles] = useState<string[] | undefined>([]);
    const [isActive, setIsActive] = useState(true);

    const { status, users } = useAppSelector((state => state.users));
    const { selectedAccount, user } = useAppSelector((state => state.auth));

    const [userAccountId, setUserAccountId] = useState([selectedAccount._id]);
    const [editUserData, setEditUserData] = useState({} as IUserFormInputs);
    const [isEditing, setIsEditing] = useState(false);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    /**
     * Method to go back to previous route.
     */
    const navigateBack = () => {
        if (state) {
            const accountId = (state as ILocationState).accountId;

            navigate(`/account/account-details/${accountId}/account-users`);

        } else {
            navigate('/users');
        }
    }

    /**
     * Method to handle user form submit.
     */
    const onSubmit = async (data: IUserFormInputs) => {
        // Dispatch add user action.
        try {
            if (!isEditing) {
                data.accounts = userAccountId;

                await dispatch(addUser(data)).unwrap();
            } else {
                await dispatch(updateUser({ userUpdatePayload: data, id: userId })).unwrap();
            }

            navigateBack();
        } catch (error: any) {
            // Empty catch block.
        }
    };

    /**
     * Method to handle Cancel button click.
     */
    const onCancel = () => {
        navigateBack();
    }

    /**
     * If state is there is means user is being created in specific account.
     * Set specific account id.
     */
    useEffect(() => {
        if (state) {
            const accountId = (state as ILocationState).accountId;
            setUserAccountId([accountId]);
        }
    }, [state]);

    /**
     * Set edit user data in case of edit.
     */
    useEffect(() => {
        if (userId) {
            setIsEditing(true);
            const user = users.find(user => user._id === userId) as IUserDetails;

            if (user) {
                const { name, email, roles, isActive } = user;
                const userRoles = roles?.map(role => role._id);
                const editUserData = { name, email, isActive, roles: userRoles, accounts: userAccountId };

                setEditUserData(editUserData);
            }
        }
    }, [userId, users, userAccountId]);

    /**
     * Fetch Roles on User form load.
     */
    useEffect(() => {
        // Method to fetch available roles.
        const fetchRoles = async () => {
            try {
                const rolesOptions = await RoleService.fetchRoles() as IOptions[];

                setRoleOptions(rolesOptions)
            } catch (error) {
                // empty catch block.
            }
        }
        fetchRoles();
    }, []);

    // Set roles in case of edit.
    useEffect(() => {
        if (isEditing && !isEmpty(editUserData)) {
            setUserRoles(editUserData.roles);
            setIsActive(editUserData.isActive);

            // Set form data with default values in case of edit.
            methods.reset(editUserData);
        }
    }, [isEditing, editUserData, methods]);

    return (
        <>
            <BreadCrum />
            <Container component="main">
                <CssBaseline />
                <Paper
                    variant="elevation"
                    elevation={2}
                    sx={{ padding: 3, mt: 2 }}
                >
                    <Typography component="h1" variant="h5" textAlign={'center'}>
                        {isEditing ? 'Edit Member' : 'Create A Member'}
                    </Typography>
                    <FormProvider {...methods}>
                        <Box
                            component="form"
                            onSubmit={methods.handleSubmit(onSubmit)}
                            noValidate
                            sx={{ mt: 1 }}
                        >
                            <CustomTextField
                                label='Name'
                                name='name'
                                required
                            />
                            <CustomTextField
                                label='Email'
                                name='email'
                                required
                            />
                            {isEditing && <CustomCheckbox
                                label='Is Active'
                                name='isActive'
                                checked={isActive ?? false}
                                onChange={(event, checked) => { setIsActive(checked) }}
                            />}
                            <CustomSelect
                                name='roles'
                                label='User Role'
                                id='user-role'
                                value={userRoles ?? ''}
                                onChange={(event: SelectChangeEvent<any>) => { 
                                    setUserRoles([event.target.value]);
                                    methods.setValue("roles", event.target.value, {shouldValidate: true});
                                }}
                                options={roleOptions}
                                disabled={userId === user?._id}
                            />

                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'end', columnGap: 2 }}>
                                <Button
                                    variant='outlined'
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                                <LoadingButton
                                    type="submit"
                                    loading={status === ApiStatus.LOADING}
                                    variant="contained"
                                    color='secondary'
                                >
                                    {isEditing ? 'Update' : 'Create'}
                                </LoadingButton>
                            </Box>
                        </Box>
                    </FormProvider>
                </Paper>
            </Container>
        </>
    );
}

export default UserForm;